<script lang="ts">
	import IMask from "imask";
	import { portal } from "design/helpers/svelte-portal";
	import { onMount, onDestroy, getContext } from "svelte";
	import { BROWSER } from "esm-env";
	import { createEventDispatcher } from "svelte";
	import {
		autoUpdate,
		computePosition,
		flip,
		shift,
		offset
	} from "@floating-ui/dom";

	export let value: string = "";
	export let label: string = "";
	export let format: string = "dd/mm/yyyy";

	const iframe = getContext<boolean>("iframe");
	const formLanguage = getContext<Language>("formLanguage");

	// const reverse = BROWSER && Boolean(navigator.language === "en-US");
	const reverse = format === "mm/dd/yyyy";

	$: {
		if (typeof internalValue === "undefined" && value) {
			let date = dayjs(value).toDate();
			if (date instanceof Date && !isNaN(date.getTime())) {
				internalValue = dateToFormat(date);
				value = dateToISO(date);
			}
		}
	}

	$: {
		// When the field is updated, update the value of the input
		if (mask && value) {
			is_empty = internalValue === "";
			const maskValue = dateToISO(formatToDate(mask._value));
			if (maskValue !== value) {
				let date = dayjs(value).toDate();
				if (date instanceof Date && !isNaN(date.getTime())) {
					inputRef.value = dateToFormat(date);
					mask.value = dateToFormat(date);
					mask.updateValue();
				}
			}
		}
	}

	/**
	 * Convert a Date object to a formatted date string in "dd/mm/yyyy" or "mm/dd/yyyy" format.
	 * @param {Date} date - The Date object to convert.
	 * @returns {string} The formatted date string.
	 */
	const dateToFormat = (date: Date): string => {
		let day = date.getDate().toString().padStart(2, "0");
		let month = (date.getMonth() + 1).toString().padStart(2, "0");
		if (reverse) {
			return [month, day, date.getFullYear()].join("/");
		}
		return [day, month, date.getFullYear()].join("/");
	};

	/**
	 * Convert a Date object to an ISO formatted date string in "yyyy-mm-dd" format.
	 * @param {Date} date - The Date object to convert.
	 * @returns {string} The ISO formatted date string.
	 */
	const dateToISO = (date: Date): string => {
		let day = date.getDate().toString().padStart(2, "0");
		let month = (date.getMonth() + 1).toString().padStart(2, "0");
		return [date.getFullYear(), month, day].join("-");
	};

	onDestroy(() => {
		if (mask) mask.destroy();
		mask = undefined;
	});

	const keyboardEventListeners = (e: KeyboardEvent) => {
		if (e.key === "Tab" && is_open) {
			is_open = false;
		}
	};

	/**
	 * Convert a date string in format "dd/mm/yyyy" or "mm/dd/yyyy" to a Date object.
	 * @param {string} str - The date string.
	 * @returns {Date} The Date object representing the input date string.
	 */
	const formatToDate = (str: string): Date => {
		let yearMonthDay = str
			.replace(/\s/g, "")
			.split("/")
			.map((x) => parseInt(x));
		if (reverse) {
			return new Date(yearMonthDay[2], yearMonthDay[0] - 1, yearMonthDay[1]);
		}
		return new Date(yearMonthDay[2], yearMonthDay[1] - 1, yearMonthDay[0]);
	};

	onDestroy(() => {
		if (!BROWSER) return;
		document.removeEventListener("keydown", keyboardEventListeners);
	});

	onMount(async () => {
		if (!BROWSER) return;
		document.addEventListener("keydown", keyboardEventListeners);
		inputRef.addEventListener("focus", (e) => {
			dispatch("updateOther", {
				active: true
			});
			is_open = true;
		});
		inputRef.addEventListener("blur", () => {
			dispatch("updateOther", {
				tainted: true
			});
			dispatch("updateOther", {
				active: false
			});
		});
		mask = IMask(inputRef, {
			mask: Date,
			pattern: reverse ? "MM/`DD/`YYYY" : "DD/`MM/`YYYY",
			eager: false,
			lazy: false,
			overwrite: true,
			min: new Date(1000, 0, 1),
			max: new Date(9999, 0, 1),
			blocks: {
				DD: {
					mask: IMask.MaskedRange,
					placeholderChar: "d",
					from: 1,
					to: 31,
					maxLength: 2,
					autofix: "pad"
				},
				MM: {
					mask: IMask.MaskedRange,
					from: 1,
					to: 12,
					placeholderChar: "m",
					maxLength: 2,
					autofix: "pad"
				},
				YYYY: {
					mask: IMask.MaskedRange,
					from: 0,
					to: 9999,
					placeholderChar: "y",
					maxLength: 4,
					autofix: "pad"
				}
			},
			format: (date: any): string => {
				return dateToFormat(date);
			},
			parse: (str: string): Date => {
				return formatToDate(str);
			}
		});
		mask.on("accept", () => {
			value = Boolean(mask.typedValue)
				? dateToISO(formatToDate(mask.value))
				: mask.value;
		});
	});
	function windowClick(e) {
		const ignore = [datepickerRef, inputRef, buttonRef];
		if (!is_open) return;
		if (ignore.some((el) => el && el.contains(e.target))) return;
		is_open = false;
	}

	$: {
		if (is_open && datepickerRef && typeof cleanup === "undefined") {
			cleanup = autoUpdate(inputRef, datepickerRef, () => {
				update();
			});
		}
		if (!is_open && typeof cleanup !== "undefined") {
			cleanup();
			cleanup = undefined;
		}
	}

	const update = async () => {
		if (!datepickerRef || !is_open) return;
		const { x, y } = await computePosition(inputRef, datepickerRef, {
			placement: "bottom-start",
			middleware: [offset(6), flip(), shift({ padding: 5 })]
		});
		if (window.innerHeight <= 360 && iframe === true) {
			deep_shadow = true;
			Object.assign(datepickerRef.style, {
				left: `50%`,
				top: `50%`,
				transform: `translate(-50%, -50%)`
			});
		} else {
			Object.assign(datepickerRef.style, {
				left: `${x}px`,
				top: `${y}px`
			});
		}
	};

	$: {
		if (is_open) {
			requestAnimationFrame(update);
		}
	}

	let internalValue: string;
	const dispatch = createEventDispatcher();
	let inputRef: HTMLInputElement;
	let datepickerRef: HTMLDivElement;
	let buttonRef: HTMLButtonElement;
	let mask: any;
	let is_open: boolean = false;
	let cleanup: CallbackFn | undefined;
	let deep_shadow: boolean = false;
	let is_empty: boolean = true;
	import dayjs from "dayjs";

	const dateSelect = (e: any) => {
		if (!e.detail) return;
		value = e.detail;
		let date = dayjs(value).toDate();
		if (date instanceof Date) {
			internalValue = dateToFormat(date);
		}
		is_open = false;
		dispatch("updateOther", {
			tainted: true
		});
	};
</script>

<input
	type="text"
	bind:this="{inputRef}"
	bind:value="{internalValue}"
	class="{is_empty ? 'empty' : ''} {$$props.class ?? ''}"
	inputmode="numeric"
	aria-label="{label}" />
{#await import("$basic/Datepicker.svelte") then Datepicker}
	{#if is_open}
		{#if deep_shadow}
			<div use:portal="{'body'}" class="deep-shadow-portal"></div>
		{/if}
		<div
			use:portal="{'body'}"
			class="form-popup shadow-dropdown"
			style="font-size: {deep_shadow
				? '1.10rem'
				: '1rem'}; position: {deep_shadow ? 'fixed' : 'absolute'}"
			class:shadow-dropdown-dark="{deep_shadow}"
			bind:this="{datepickerRef}">
			<svelte:component
				this="{Datepicker.default}"
				locale="{formLanguage.datepicker}"
				on:change="{dateSelect}"
				value="{value}" />
		</div>
	{/if}
{/await}
